import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createSlot, createBulkSlots } from '../../redux/slotSlice';
import { fetchPartnerGyms } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { TimePicker } from '../../components/TimePicker';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

export const CreateSlotPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state: RootState) => state.slot);
  const { gyms } = useSelector((state: RootState) => state.gym);
  const [successMessage, setSuccessMessage] = useState('');
  const [creationMode, setCreationMode] = useState<'single' | 'bulk'>('single');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Single slot form
  const [singleSlotData, setSingleSlotData] = useState({
    gymId: '',
    date: '',
    start_time: '06:00:00',
    end_time: '08:00:00',
    capacity: '',
    price_coins: '',
    price_cash: ''
  });

  // Bulk slots form
  const [bulkSlotData, setBulkSlotData] = useState({
    gymId: '',
    start_date: '',
    end_date: '',
    days_of_week: [] as number[],
    time_slots: [
      {
        start_time: '06:00:00',
        end_time: '08:00:00',
        capacity: '',
        price_coins: '',
        price_cash: ''
      }
    ]
  });

  useEffect(() => {
    dispatch(fetchPartnerGyms());
  }, [dispatch]);

  const handleSingleSlotChange = (field: string, value: string) => {
    setSingleSlotData({ ...singleSlotData, [field]: value });
    if (fieldErrors[field]) {
      setFieldErrors({ ...fieldErrors, [field]: '' });
    }
  };

  const handleBulkSlotChange = (field: string, value: any) => {
    setBulkSlotData({ ...bulkSlotData, [field]: value });
    if (fieldErrors[field]) {
      setFieldErrors({ ...fieldErrors, [field]: '' });
    }
  };

  const handleDayToggle = (day: number) => {
    const currentDays = bulkSlotData.days_of_week;
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];
    setBulkSlotData({ ...bulkSlotData, days_of_week: newDays });
  };

  const handleTimeSlotChange = (index: number, field: string, value: string) => {
    const newTimeSlots = [...bulkSlotData.time_slots];
    newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };
    setBulkSlotData({ ...bulkSlotData, time_slots: newTimeSlots });
  };

  const addTimeSlot = () => {
    setBulkSlotData({
      ...bulkSlotData,
      time_slots: [
        ...bulkSlotData.time_slots,
        {
          start_time: '06:00:00',
          end_time: '08:00:00',
          capacity: '',
          price_coins: '',
          price_cash: ''
        }
      ]
    });
  };

  const removeTimeSlot = (index: number) => {
    const newTimeSlots = bulkSlotData.time_slots.filter((_, i) => i !== index);
    setBulkSlotData({ ...bulkSlotData, time_slots: newTimeSlots });
  };

  const validateSingleSlot = (): boolean => {
    const errors: Record<string, string> = {};

    if (!singleSlotData.gymId) errors.gymId = 'Please select a gym';
    if (!singleSlotData.date) errors.date = 'Date is required';
    if (!singleSlotData.capacity || parseInt(singleSlotData.capacity) < 1) {
      errors.capacity = 'Capacity must be at least 1';
    }
    if (!singleSlotData.price_coins || parseInt(singleSlotData.price_coins) < 0) {
      errors.price_coins = 'Price in coins is required';
    }
    if (!singleSlotData.price_cash || parseFloat(singleSlotData.price_cash) < 0) {
      errors.price_cash = 'Price in cash is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateBulkSlots = (): boolean => {
    const errors: Record<string, string> = {};

    if (!bulkSlotData.gymId) errors.gymId = 'Please select a gym';
    if (!bulkSlotData.start_date) errors.start_date = 'Start date is required';
    if (!bulkSlotData.end_date) errors.end_date = 'End date is required';
    if (bulkSlotData.days_of_week.length === 0) {
      errors.days_of_week = 'Select at least one day';
    }
    if (bulkSlotData.time_slots.length === 0) {
      errors.time_slots = 'Add at least one time slot';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (creationMode === 'single') {
      if (!validateSingleSlot()) return;

      const result = await dispatch(
        createSlot({
          gymId: parseInt(singleSlotData.gymId),
          data: {
            date: singleSlotData.date,
            start_time: singleSlotData.start_time,
            end_time: singleSlotData.end_time,
            capacity: parseInt(singleSlotData.capacity),
            price_coins: parseInt(singleSlotData.price_coins),
            price_cash: parseFloat(singleSlotData.price_cash)
          }
        })
      );

      if (result.type === createSlot.fulfilled.type) {
        setSuccessMessage('Slot created successfully!');
        setTimeout(() => navigate('/slots'), 1500);
      }
    } else {
      if (!validateBulkSlots()) return;

      const result = await dispatch(
        createBulkSlots({
          gymId: parseInt(bulkSlotData.gymId),
          data: {
            start_date: bulkSlotData.start_date,
            end_date: bulkSlotData.end_date,
            days_of_week: bulkSlotData.days_of_week,
            time_slots: bulkSlotData.time_slots.map((slot) => ({
              start_time: slot.start_time,
              end_time: slot.end_time,
              capacity: parseInt(slot.capacity),
              price_coins: parseInt(slot.price_coins),
              price_cash: parseFloat(slot.price_cash)
            }))
          }
        })
      );

      if (result.type === createBulkSlots.fulfilled.type) {
        setSuccessMessage('Slots created successfully!');
        setTimeout(() => navigate('/slots'), 1500);
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/slots')}
          sx={{ mb: 2 }}
        >
          Back to Slots
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Create Slots
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create single or multiple recurring slots for your gym
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Creation Mode Toggle */}
      <Box sx={{ mb: 4 }}>
        <ToggleButtonGroup
          value={creationMode}
          exclusive
          onChange={(_, value) => value && setCreationMode(value)}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <ToggleButton value="single" sx={{ flex: { xs: 1, sm: 'initial' }, px: 3 }}>
            Single Slot
          </ToggleButton>
          <ToggleButton value="bulk" sx={{ flex: { xs: 1, sm: 'initial' }, px: 3 }}>
            Bulk Slots
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Gym Selection (Common) */}
          <Grid item xs={12}>
            <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Gym Selection
              </Typography>
              <FormControl fullWidth error={!!fieldErrors.gymId}>
                <InputLabel>Select Gym *</InputLabel>
                <Select
                  value={creationMode === 'single' ? singleSlotData.gymId : bulkSlotData.gymId}
                  onChange={(e) =>
                    creationMode === 'single'
                      ? handleSingleSlotChange('gymId', e.target.value)
                      : handleBulkSlotChange('gymId', e.target.value)
                  }
                  label="Select Gym *"
                >
                  {gyms.map((gym) => (
                    <MenuItem key={gym.id} value={gym.id.toString()}>
                      {gym.name} - {gym.city}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.gymId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {fieldErrors.gymId}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Single Slot Form */}
          {creationMode === 'single' && (
            <>
              <Grid item xs={12}>
                <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Slot Details
                  </Typography>
                  <Grid container spacing={{ xs: 3, sm: 3, md: 3.5 }} sx={{ '& .MuiGrid-item': { paddingTop: '24px !important' } }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date"
                        type="date"
                        value={singleSlotData.date}
                        onChange={(e) => handleSingleSlotChange('date', e.target.value)}
                        required
                        error={!!fieldErrors.date}
                        helperText={fieldErrors.date}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Capacity"
                        type="number"
                        value={singleSlotData.capacity}
                        onChange={(e) => handleSingleSlotChange('capacity', e.target.value)}
                        required
                        error={!!fieldErrors.capacity}
                        helperText={fieldErrors.capacity}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Start Time"
                        value={singleSlotData.start_time}
                        onChange={(value) => handleSingleSlotChange('start_time', value)}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="End Time"
                        value={singleSlotData.end_time}
                        onChange={(value) => handleSingleSlotChange('end_time', value)}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Price (Coins)"
                        type="number"
                        value={singleSlotData.price_coins}
                        onChange={(e) => handleSingleSlotChange('price_coins', e.target.value)}
                        required
                        error={!!fieldErrors.price_coins}
                        helperText={fieldErrors.price_coins}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Price (Cash ₹)"
                        type="number"
                        value={singleSlotData.price_cash}
                        onChange={(e) => handleSingleSlotChange('price_cash', e.target.value)}
                        required
                        error={!!fieldErrors.price_cash}
                        helperText={fieldErrors.price_cash}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </>
          )}

          {/* Bulk Slots Form */}
          {creationMode === 'bulk' && (
            <>
              <Grid item xs={12}>
                <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Date Range
                  </Typography>
                  <Grid container spacing={{ xs: 3, sm: 3, md: 3.5 }} sx={{ '& .MuiGrid-item': { paddingTop: '24px !important' } }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={bulkSlotData.start_date}
                        onChange={(e) => handleBulkSlotChange('start_date', e.target.value)}
                        required
                        error={!!fieldErrors.start_date}
                        helperText={fieldErrors.start_date}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={bulkSlotData.end_date}
                        onChange={(e) => handleBulkSlotChange('end_date', e.target.value)}
                        required
                        error={!!fieldErrors.end_date}
                        helperText={fieldErrors.end_date}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Days of Week
                  </Typography>
                  <FormGroup row>
                    {DAYS_OF_WEEK.map((day) => (
                      <FormControlLabel
                        key={day.value}
                        control={
                          <Checkbox
                            checked={bulkSlotData.days_of_week.includes(day.value)}
                            onChange={() => handleDayToggle(day.value)}
                          />
                        }
                        label={day.label}
                      />
                    ))}
                  </FormGroup>
                  {fieldErrors.days_of_week && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {fieldErrors.days_of_week}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Time Slots
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={addTimeSlot}
                      size="small"
                      sx={{ height: '36px' }}
                    >
                      + Add Time Slot
                    </Button>
                  </Box>

                {bulkSlotData.time_slots.map((slot, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      position: 'relative'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Time Slot {index + 1}
                    </Typography>
                    <Grid container spacing={{ xs: 3, sm: 3, md: 3.5 }} sx={{ '& .MuiGrid-item': { paddingTop: '24px !important' } }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TimePicker
                          label="Start Time"
                          value={slot.start_time}
                          onChange={(value) => handleTimeSlotChange(index, 'start_time', value)}
                          required
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TimePicker
                          label="End Time"
                          value={slot.end_time}
                          onChange={(value) => handleTimeSlotChange(index, 'end_time', value)}
                          required
                        />
                      </Grid>

                      <Grid item xs={12} sm={4} md={2}>
                        <TextField
                          fullWidth
                          label="Capacity"
                          type="number"
                          value={slot.capacity}
                          onChange={(e) => handleTimeSlotChange(index, 'capacity', e.target.value)}
                          required
                          inputProps={{ min: 1 }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4} md={2}>
                        <TextField
                          fullWidth
                          label="Coins"
                          type="number"
                          value={slot.price_coins}
                          onChange={(e) => handleTimeSlotChange(index, 'price_coins', e.target.value)}
                          required
                          inputProps={{ min: 0 }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4} md={2}>
                        <TextField
                          fullWidth
                          label="Cash ₹"
                          type="number"
                          value={slot.price_cash}
                          onChange={(e) => handleTimeSlotChange(index, 'price_cash', e.target.value)}
                          required
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Grid>
                    </Grid>

                    {bulkSlotData.time_slots.length > 1 && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeTimeSlot(index)}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                ))}
                </Box>
              </Grid>
            </>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/slots')}
                sx={{ height: '48px', minWidth: { xs: '100%', sm: '140px' }, order: { xs: 2, sm: 1 } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                sx={{
                  height: '48px',
                  minWidth: { xs: '100%', sm: '160px' },
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  },
                  order: { xs: 1, sm: 2 }
                }}
              >
                {isLoading ? 'Creating...' : creationMode === 'single' ? 'Create Slot' : 'Create Slots'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default CreateSlotPage;
